import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import StyleTemplateEditor from './StyleTemplateEditor';
import DataConnectionAdmin from './DataConnectionAdmin';
import DataModelAdmin from './DataModelAdmin';
import ChartBuilderReferenceAdmin from './ChartBuilderReferenceAdmin';
import TimeRangeSetsAdmin from './TimeRangeSetsAdmin';
import ReportEmailConfigAdmin from './ReportEmailConfigAdmin';
import DashboardAccessAdmin from './DashboardAccessAdmin';

function TabPanel({ children, value, index, ...rest }) {
  // No desmontar al cambiar de tab: DataModelAdmin (y otros) pierden ediciones
  // sin guardar y la posicion seleccionada si se recrean desde cero cada vez.
  // El atributo "hidden" del div ya oculta visualmente el contenido inactivo.
  return (
    <div role="tabpanel" hidden={value !== index} {...rest}>
      <Box sx={{ pt: 1 }}>{children}</Box>
    </div>
  );
}

const AdminPanel = ({ open, onClose, getAuthHeaders }) => {
  const [tabValue, setTabValue] = useState(0);
  const dataModelRef = useRef(null);
  const [dataModelStatus, setDataModelStatus] = useState({
    dirty: false,
    saving: false,
    canSave: false,
  });

  const handleDataModelStatusChange = useCallback((status) => {
    setDataModelStatus(status);
  }, []);

  const isDataModelTab = tabValue === 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: '92vh', width: '95vw' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon /> Configuración (solo admin)
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
        >
          <Tab label="Apariencia" />
          <Tab label="Conexiones y datos" />
          <Tab label="Modelo de datos" />
          <Tab label="Editor de gráficos" />
          <Tab label="Rangos horarios" />
          <Tab label="Reportes" />
          <Tab label="Accesos dashboards" />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <StyleTemplateEditor open={open && tabValue === 0} onClose={onClose} embedded />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <DataConnectionAdmin open={open && tabValue === 1} getAuthHeaders={getAuthHeaders} embedded />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <DataModelAdmin
            ref={dataModelRef}
            open={open && tabValue === 2}
            getAuthHeaders={getAuthHeaders}
            onStatusChange={handleDataModelStatusChange}
            embedded
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <ChartBuilderReferenceAdmin open={open && tabValue === 3} getAuthHeaders={getAuthHeaders} embedded />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <TimeRangeSetsAdmin open={open && tabValue === 4} getAuthHeaders={getAuthHeaders} embedded />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <ReportEmailConfigAdmin open={open && tabValue === 5} getAuthHeaders={getAuthHeaders} embedded />
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <DashboardAccessAdmin open={open && tabValue === 6} getAuthHeaders={getAuthHeaders} embedded />
        </TabPanel>
      </DialogContent>
      {isDataModelTab && (
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={
              dataModelStatus.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
            }
            onClick={() => dataModelRef.current?.save()}
            disabled={!dataModelStatus.canSave}
          >
            Guardar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AdminPanel;
